import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PlayerMatch } from "../../models/PlayerMatch";
import { Team } from "../../models/Team";
import useLocalStorage from 'use-local-storage';

type StatView = 'full' | 't-side' | 'ct-side';

interface ScoreboardProps {
    team: Team;
    players: PlayerMatch[];
    teamScore: number | undefined; // Add teamScore prop
}

const Scoreboard: React.FC<ScoreboardProps> = ({ team, players, teamScore }) => {
    const [isDark] = useLocalStorage<boolean>("isDark", false);
    const [statView, setStatView] = useState<StatView>('full');
    
    players.sort((a, b) => b.rating2 - a.rating2);

    // Check if T/CT side data is available
    const hasTSideData = players.some(p => p.tSideKills !== undefined);
    const hasCTSideData = players.some(p => p.ctSideKills !== undefined);
    const hasRating21 = players.some(p => p.rating21 !== undefined && p.rating21 !== 0);

    // Get stats based on current view
    const getPlayerStats = (player: PlayerMatch) => {
        switch (statView) {
            case 't-side':
                return {
                    kills: player.tSideKills || 0,
                    deaths: player.tSideDeaths || 0,
                    assists: player.tSideAssists || 0,
                    adr: player.tSideADR || 0,
                    kast: player.tSideKAST || 0,
                    rating2: player.tSideRating2 || 0,
                    rating21: player.tSideRating21 || 0
                };
            case 'ct-side':
                return {
                    kills: player.ctSideKills || 0,
                    deaths: player.ctSideDeaths || 0,
                    assists: player.ctSideAssists || 0,
                    adr: player.ctSideADR || 0,
                    kast: player.ctSideKAST || 0,
                    rating2: player.ctSideRating2 || 0,
                    rating21: player.ctSideRating21 || 0
                };
            default:
                return {
                    kills: player.kills,
                    deaths: player.deaths,
                    assists: player.assists,
                    adr: player.adr,
                    kast: player.kast,
                    rating2: player.rating2,
                    rating21: player.rating21 || 0
                };
        }
    };

    return (
        <>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <Link to={`/team/${team.teamId}`} className="text-decoration-none team-name">
                    <div className="d-flex align-items-center">
                        <img src={team.teamLogo} alt="TeamLogo" style={{ width: 40 }} />
                        <h4 className="ms-2">
                            {team.teamName}
                            {
                                teamScore !== undefined ? (
                                    ` (${teamScore})`
                                ) : null
                            }
                        </h4>
                    </div>
                </Link>
                
                {(hasTSideData || hasCTSideData) && (
                    <div className="btn-group btn-group-sm" role="group" style={{ marginLeft: 'auto' }}>
                        <button
                            className={`btn ${statView === 'full' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => setStatView('full')}
                            style={{ minWidth: '60px' }}
                        >
                            Full
                        </button>
                        {hasTSideData && (
                            <button
                                className={`btn ${statView === 't-side' ? 'btn-warning' : 'btn-outline-warning'}`}
                                onClick={() => setStatView('t-side')}
                                style={{ minWidth: '70px' }}
                            >
                                T-Side
                            </button>
                        )}
                        {hasCTSideData && (
                            <button
                                className={`btn ${statView === 'ct-side' ? 'btn-info' : 'btn-outline-info'}`}
                                onClick={() => setStatView('ct-side')}
                                style={{ minWidth: '80px' }}
                            >
                                CT-Side
                            </button>
                        )}
                    </div>
                )}
            </div>
            
            <table
                className={`table table-striped ${isDark ? 'table-dark' : ''}`}
                style={
                    isDark
                        ? {
                            border: '1px solid #111',
                            borderCollapse: 'collapse' as const,
                        }
                        : undefined
                }
            >
                <thead>
                    <tr>
                        <th>Navn</th>
                        <th>K/D/A</th>
                        <th>+/-</th>
                        <th>ADR</th>
                        <th>KAST%</th>
                        <th>Rating 2.0</th>
                        {hasRating21 && <th>Rating 2.1</th>}
                    </tr>
                </thead>
                <tbody>
                    {players.map((player, index) => {
                        const stats = getPlayerStats(player);
                        return (
                            <tr key={index}>
                                <td>
                                    <Link to={`/player/${player.steamId}`} className={`text-decoration-none ${isDark ? 'text-light' : 'text-dark'}`}>
                                        {player.account?.playerName || player.steamId}
                                    </Link>
                                </td>
                                <td>{stats.kills}/{stats.deaths}/{stats.assists}</td>
                                <td>{stats.kills - stats.deaths}</td>
                                <td>{Math.round(stats.adr * 100) / 100}</td>
                                <td>{Math.round(stats.kast * 100) / 100}%</td>
                                <td>{Math.round(stats.rating2 * 100) / 100}</td>
                                {hasRating21 && <td>{stats.rating21 > 0 ? Math.round(stats.rating21 * 100) / 100 : '-'}</td>}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </>
    );
};

export default Scoreboard;
